const constants = {
    pageHeaderHeightNumber: 45,
    appMinWidth: '1280px',
    get pageHeaderHeight() {
        return `${this.pageHeaderHeightNumber}px`;
    },
    pageHeaderMargin: '0.5rem',
    // Admin page layout constants
    adminTabsHeight: '42px',
    adminContentPadding: '1rem',
};

/**
 * Responsive breakpoint values (in px).
 * Used for consistent responsive design across the application.
 */
export const breakpoints = {
    mobile: 768,
    tablet: 1024,
    desktop: 1280,
} as const;

/**
 * Media query helpers for responsive design.
 *
 * "mobile" queries use `(pointer: coarse)` to avoid triggering on laptops
 * with large font sizes / small screens. This ensures the mobile UI
 * (hamburger menu, hidden nav) only activates on actual touch devices.
 * `pointer: coarse` detects whether the primary input is a finger (touch)
 * vs a mouse (fine pointer) and is more reliably reported than `hover: none`.
 *
 * "narrow" queries are width-only for layout adaptations that should
 * apply regardless of device type when space is limited.
 */
export const media = {
    /** Touch device with narrow viewport — use for mobile-only UI (hamburger, hidden nav) */
    mobile: `@media (max-width: ${breakpoints.mobile}px) and (pointer: coarse)`,
    /** Narrow viewport regardless of device — use for layout reflow (grid collapse, font reduction) */
    narrow: `@media (max-width: ${breakpoints.mobile}px)`,
    /** Tablet-ish width — use for intermediate layout adjustments */
    tablet: `@media (max-width: ${breakpoints.tablet}px)`,
} as const;

/**
 * Check if the current device is a touch device (coarse pointer).
 * Use in JavaScript when you need to distinguish mobile from desktop at runtime.
 */
export function isTouchDevice(): boolean {
    return window.matchMedia('(pointer: coarse)').matches;
}

const calculatedPageHeaderSize = `calc(${constants.pageHeaderHeight} + ${constants.pageHeaderMargin})`;

const colors = {
    text: {
        primary: 'rgb(4, 38, 90)',
        heading: 'rgb(0, 68, 126)',
        panelHeading: 'rgb(255, 255, 255)',
        error: 'rgb(255, 0, 0)',
        softError: 'rgb(255, 165, 0)',
        tabText: 'rgb(31, 44, 3)',
        mrvVerified: 'rgb(57, 54, 10)',
        portName: 'rgb(4, 38, 90)',
        summary: 'rgb(0, 68, 126)',
        purple: 'rgb(128, 0, 128)',
        khaki: 'rgb(183, 173, 86)',
        pink: 'rgb(230, 81, 0)',
        lightGrey: 'rgb(136, 136, 136)',
        italicGrey: 'rgb(170, 170, 170)',
        white: 'rgb(255, 255, 255)',
        green: 'rgb(0, 128, 0)',
        lightBlue: 'rgb(4, 38, 90)',
        black: 'rgb(13, 22, 46)',
        blueLink: 'rgb(24, 106, 175)',
        tooltipHeader: 'rgb(255, 255, 255)',
        tabLineButton: 'rgb(4, 38, 90)',
        plainButton: 'rgb(4, 38, 90)',
        redIcon: 'rgb(255, 0, 0)',
        fileLink: 'rgb(84, 143, 205)',
        fileLinkHover: 'rgb(0, 86, 179)',
        odfjellSection: 'rgb(48, 44, 5)',
        warningDark: 'rgb(57, 43, 6)',
        dropdown: {
            value: 'rgb(51, 51, 51)',
        },
    },
    background: {
        selected: 'rgb(185, 220, 255)',
        heading: 'rgb(255, 255, 255)',
        panelHeading: 'rgb(0, 68, 126)',
        dependency: 'rgb(224, 224, 224)',
        grey: 'rgb(204, 204, 204)',
        lightGrey: 'rgb(238, 238, 238)',
        header: 'rgb(244, 244, 244)',
        hover: {
            default: 'rgb(238, 238, 238)',
            link: 'rgb(24, 106, 175)',
        },
        highlight: {
            yellow: 'rgb(255, 235, 59)',
            hover: 'rgb(245, 245, 245)',
        },
        error: 'rgb(135, 58, 69)',
        summary: 'rgb(228, 228, 228)',
        summaryHeader: 'rgb(244, 244, 244)',
        altRow: 'rgb(239, 243, 247)',
        logTime: 'rgb(10, 165, 220)',
        white: 'rgb(255, 255, 255)',
        transparent: 'transparent',
        lightYellow: 'rgb(255, 250, 205)',
        orange: 'rgb(255, 165, 0)',
        menu: {
            default: 'rgb(221, 221, 221)',
        },
        rotationTree: {
            item: 'rgb(248, 248, 248)',
        },
        menuItem: 'rgb(233, 240, 254)',
        boxShadow: 'rgb(221, 221, 221)',
        portCallBorder: 'rgb(221, 221, 221)',
        approvedStatus: 'rgb(13, 22, 46)',
        manualNode: 'rgb(13, 22, 46)',
        warning: 'rgb(248, 215, 218)',
        portNode: 'rgb(225, 216, 132)',
        atSeaNode: 'rgb(182, 211, 241)',
        settings: 'rgb(238, 238, 238)',
        ccc: 'rgb(204, 204, 204)',
        eee: 'rgb(238, 238, 238)',
        fafafa: 'rgb(250, 250, 250)',
        tableHeader: 'rgb(238 238 238)',
        odfjellPanel: {
            menuBackground: 'rgb(19, 95, 159)',
        },
        green: 'rgb(0, 128, 0)',
        darkBlue: 'rgb(19, 95, 159)',
        darkGreen: 'rgb(224, 233, 204)',
        cream: 'rgb(250, 250, 250)',
        hardRuleWarning: 'rgb(255, 0, 0)',
        softRuleWarning: 'rgb(255, 165, 0)',
        tankPlan: {
            fill: 'rgb(255, 255, 255)',
            progressBar: 'rgb(153, 153, 153)',
            progressBarInner: 'rgb(0, 0, 0)',
            menuBackground: 'rgb(255, 255, 255)',
            tankInfoBackground: 'rgb(250, 250, 250)',
        },
        tankColumns: 'rgb(238, 238, 238)',
        tooltipHeader: 'rgb(0, 0, 124)',
        odfjellSection: 'rgb(255, 255, 255)',
        plainButton: {
            default: 'rgb(255, 255, 255)',
            hover: 'rgb(238, 238, 238)',
        },
        selectedButton: 'rgb(0, 0, 124)',
        panelSection: 'rgb(244, 244, 244)',
    },
    border: {
        primary: 'rgb(204, 204, 204)',
        hover: 'rgb(187, 187, 187)',
        selected: 'rgb(0, 0, 124)',
        dependency: 'rgb(170, 170, 170)',
        grey: 'rgb(170, 170, 170)',
        orange: 'rgb(243, 160, 117)',
        light: 'rgb(221, 221, 221)',
        error: 'rgb(231, 84, 84)',
        input: 'rgb(164, 164, 195)',
        inputHover: 'rgb(71, 140, 220)',
        summaryHeader: 'rgb(0, 68, 126)',
        transparent: 'transparent',
        lightGrey: 'rgb(238, 238, 238)',
        mediumGrey: 'rgb(187, 187, 187)',
        defaultButton: 'transparent',
        boxShadow: 'rgb(204, 204, 204)',
        portCall: 'rgb(151, 167, 189)',
        opertionList: 'rgb(193 204 219)',
        menuItem: 'rgb(176, 183, 193)',
        settings: 'rgb(221, 221, 221)',
        tank: {
            cofferdam: 'rgb(13, 22, 46)',
            progressBar: 'rgb(153, 153, 153)',
        },
        dashed: 'rgb(204, 204, 204)',
        odfjellSection: 'rgb(176, 189, 148)',
        icon: 'rgb(204, 204, 204)',
        dropdown: 'rgb(204, 204, 204)',
        panel: 'rgb(221, 221, 221)',
        button: 'rgb(204, 204, 204)',
        rightStyle: 'rgb(176, 183, 193)',
        aaa: 'rgb(170,170,170)',
        ccc: 'rgb(204, 204, 204)',
        eee: 'rgb(238, 238, 238)',
    },
    status: {
        atSea: 'rgb(146, 193, 227)',
        inPort: 'rgb(225, 216, 132)',
        mismatch: 'rgb(217, 76, 76)',
        error: 'rgb(217, 76, 76)',
        arrival: 'rgb(81, 169, 79)',
        departure: 'rgb(170, 170, 170)',
        success: 'rgb(0, 128, 0)',
        warning: 'rgb(255, 165, 0)',
        errorRed: 'rgb(218, 72, 72)',
        noticeIcon: 'rgb(255, 69, 0)',
    },
    brand: {
        darkBlue: 'rgb(0, 68, 126)',
        lightBlue: 'rgb(0, 159, 223)',
        orange: 'rgb(236, 104, 37)',
    },
    shadow: {
        light: 'rgb(204, 204, 204)',
        mediumGrey: 'rgb(204, 204, 204)',
        lightGrey: 'rgb(238, 238, 238)',
        ccc: 'rgb(204, 204, 204)',
        ddd: 'rgb(221, 221, 221)',
        eee: 'rgb(238, 238, 238)',
        splitter: 'rgb(102, 102, 102)',
        panel: 'rgb(204, 204, 204)',
        splitterVertical: 'rgb(102, 102, 102)',
        button: 'rgb(238, 238, 238)',
    },
    rotationTree: {
        nodeText: 'rgb(4, 38, 90)',
        portNode: {
            border: 'rgb(225, 216, 132)',
            atSea: 'rgb(182, 211, 241)',
        },
        manualNodeBorder: 'rgb(170, 170, 136)',
        movedToBorder: 'rgb(183, 173, 86)',
    },
    button: {
        default: {
            border: 'rgb(221, 221, 221)',
            background: 'rgb(255, 255, 255)',
            color: 'rgb(4, 38, 90)',
            shadow: 'rgb(187, 187, 187)',
        },
        hover: {
            background: 'rgb(238, 238, 238)',
        },
        disabled: {
            background: 'rgb(204, 204, 204)',
            color: 'rgb(102, 102, 102)',
        },
        text: {
            normal: 'rgb(4, 38, 90)',
            disabled: 'rgb(102, 102, 102)',
        },
        border: {
            transparent: 'transparent',
            normal: 'rgb(204, 204, 204)',
        },
        exitButton: {
            hover: 'rgb(224, 233, 204)',
        },
        printerButton: {
            background: 'rgb(255, 255, 255)',
            color: 'rgb(4, 38, 90)',
        },
        toggleSummary: {
            background: 'rgb(255, 255, 255)',
            color: 'rgb(4, 38, 90)',
        },
        icon: {
            hover: 'rgb(238, 238, 238)',
        },
    },
    verification: {
        header: {
            border: 'rgb(0, 68, 126)',
            background: 'rgb(244, 244, 244)',
        },
        item: {
            background: 'rgb(250, 250, 250)',
            border: 'rgb(221, 221, 221)',
            shadow: 'rgb(204, 204, 204)',
            selected: 'rgb(222, 236, 246)',
        },
        status: {
            warning: 'rgb(255, 140, 0)',
            success: 'rgb(0, 128, 0)',
            error: 'rgb(255, 0, 0)',
        },
        comment: {
            error: 'rgb(218, 72, 72)',
        },
    },
    dropDown: {
        background: 'rgb(255, 255, 255)',
        border: 'rgb(204, 204, 204)',
        status: {
            master: 'rgb(0, 128, 0)',
            draft: 'rgb(165, 42, 42)',
            published: 'rgb(0, 0, 139)',
            shared: 'rgb(128, 0, 128)',
        },
        item: {
            hover: 'rgb(238, 238, 238)',
            infoIcon: {
                background: 'rgb(51, 51, 51)',
                color: 'rgb(255, 255, 255)',
            },
        },
    },
    tableRow: {
        shadow: 'rgb(221, 221, 221)',
        border: 'rgb(221, 221, 221)',
        background: {
            selected: 'rgb(222, 236, 246)',
            hover: 'rgb(238, 238, 238)',
        },
    },
    panel: {
        border: 'rgb(204, 204, 204)',
        shadow: 'rgb(204, 204, 204)',
        splitter: {
            background: 'rgb(102, 102, 102)',
        },
        separator: 'rgb(238, 238, 238)',
    },
    icon: {
        unsaved: 'transparent',
        saved: 'rgb(170, 170, 170)',
        edited: 'rgb(109, 152, 186)',
        completed: 'rgb(245, 171, 35)',
        approved: 'rgb(16, 124, 16)',
        tankersMismatch: 'rgb(135, 58, 69)',
        incomplete: 'rgb(255, 165, 0)',
        missing: 'rgb(255, 69, 0)',
    },
    overlays: {
        paper: 'rgb(255, 255, 255)',
        hover: 'rgb(238, 238, 238)',
    },
    pageHeader: {
        masterButton: {
            selected: 'rgb(0, 0, 124)',
            background: 'rgb(255, 255, 255)',
            text: 'rgb(255, 255, 255)',
        },
        extraButton: {
            background: 'rgb(238, 238, 238)',
            selected: 'rgb(0, 0, 124)',
            selectedText: 'rgb(255, 255, 255)',
        },
        shareStowagePanel: {
            heading: 'rgb(136, 136, 136)',
            text: 'rgb(34, 34, 34)',
            button: {
                border: 'rgb(0, 0, 124)',
                text: 'rgb(0, 0, 124)',
            },
        },
    },
    aol: {
        wrapper: {
            box: {
                shadow: 'rgb(221, 221, 221)',
            },
        },
        verification: {
            table: {
                shadow: 'rgb(221, 221, 221)',
                selectedBackground: 'rgb(222, 236, 246)',
            },
            issue: {
                background: 'rgb(250, 250, 250)',
                orange: 'rgb(255, 165, 0)',
                green: 'rgb(0, 128, 0)',
                red: 'rgb(255, 0, 0)',
                white: 'rgb(255, 255, 255)',
            },
            status: {
                green: 'rgb(0, 128, 0)',
                accepted: 'rgb(0, 68, 126)',
            },
        },
        button: {
            green: 'rgb(0, 128, 0)',
            statusMessage: 'rgb(0, 128, 0)',
        },
    },
    stowage: {
        splitterVertical: 'rgb(102, 102, 102)',
        splitterHorizontal: 'rgb(102, 102, 102)',
        rotationView: {
            shadow: 'rgb(204, 204, 204)',
        },
        rightView: {
            shadow: 'rgb(204, 204, 204)',
        },
        tankPlanView: {
            shadow: 'rgb(204, 204, 204)',
        },
        popOutButton: {
            border: 'rgb(204, 204, 204)',
        },
    },
};

// For backward compatibility
const legacyColors = {
    primaryTextColor: colors.text.primary,
    blackTextColor: colors.text.black,
    selectedBackground: colors.background.selected,
    selectedColor: colors.text.primary,
    selectedBorder: colors.border.selected,
    headingColor: colors.text.heading,
    headingPanelBgColor: colors.background.heading,
    panelHeadingBackground: colors.background.panelHeading,
    panelHeadingTextColor: colors.text.panelHeading,
    darkBlue: colors.brand.darkBlue,
    lightBlue: colors.brand.lightBlue,
    orange: colors.brand.orange,
    atSea: colors.status.atSea,
    inPort: colors.status.inPort,
    mismatch: colors.status.mismatch,
    error: colors.status.error,
    errorText: colors.text.error,
    softErrorText: colors.text.softError,
    arrival: colors.status.arrival,
    departure: colors.status.departure,
    dependencyBackground: colors.background.dependency,
    dependencyColor: colors.text.primary,
    dependencyBorder: colors.border.dependency,
    greyBorderColor: colors.border.grey,
    orangeBorderColor: colors.border.orange,
    greyBackground: colors.background.grey,
    lightGreyBackground: colors.background.lightGrey,
    headerBackground: colors.background.header,
    hoverBackground: colors.background.hover.default,
    highlightBackground: colors.background.highlight,
};

const selectableElement = (isSelected: boolean) => `
    cursor: pointer;
    transition: background-color 50ms cubic-bezier(0.4, 0, 0.2, 1);
    ${isSelected
        ? `background: ${colors.background.selected} !important; 
         color: ${colors.text.primary};
         `
        : `
         &:hover{
            background: ${colors.background.hover.default};
         }
         &:active {
             transform: scale(0.99);
             transition: color 200ms ease-in 50ms
         }
         `
    }
    &:hover{
        background: ${colors.background.hover.default};
    }
`;


const clickableIcon = () => `
    cursor: pointer;
    transition: color 50ms cubic-bezier(0.4, 0, 0.2, 1);

    &:active {
        transform: scale(0.9);
        transition: color 200ms ease-in 50ms
    }
    &:hover{
        color: ${colors.background.hover.link};
    }
`;

const inlineGridWrapper = `
    display: inline-grid;
    grid-auto-flow: column;
    grid-column-gap: 1rem;
`;

const windowHeight = window.outerHeight.toString + 'px';

const fontWeights = {
    fileLink: 500,
};

export default {
    legacyColors: legacyColors,
    colors: colors,
    constants: constants,
    fontWeights: fontWeights,
    inlineGridWrapper,
    calculatedPageHeaderSize: calculatedPageHeaderSize,
    contentHeight: `calc(${windowHeight} - ${calculatedPageHeaderSize})`,
    primaryTextColor: colors.brand.darkBlue,
    selectableElement: selectableElement,
    clickableIcon: clickableIcon,
};
